import React from "react";

import type { FormInputType, PreFilledFieldsType } from "@ui/components/ContactForm";

import type { OtPurpose } from "@jti/fe-common";

export type ContactFormValues = Record<
  string,
  string | boolean | { label?: string; value?: string; checked?: boolean }
>;

export const replaceSelectValuesWithString = (formValues: ContactFormValues) => {
  return removeFalsyValues(
    Object.fromEntries(
      Object.entries(formValues).map(([key, value]) => {
        if (typeof value === "object") {
          return [key, value.value];
        }
        return [key, value];
      })
    )
  );
};

export const removeFalsyValues = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => Boolean(value))) as Partial<T>;
};

export const transformDataWithConsents = <T extends Record<string, unknown>>(data: T, consents: OtPurpose[]) => {
  if (!consents.length) {
    return data;
  }

  const consentPurposes = consents.map((consent) => ({
    accepted: data[consent.id] === true,
    id: consent.id,
  }));

  const result = {
    ...data,
    consent_purposes: consentPurposes,
  };

  consents.forEach((consent) => {
    delete result[consent.id];
  });

  return result;
};

export const removeWhitespacesFromPhoneNumber = <T extends Record<string, unknown>>(values: T): T =>
  Object.entries(values).reduce(
    (acc, [key, value]) => {
      if (typeof value === "string" && /^\+?\d[\d\s]*$/.test(value)) {
        acc[key] = value.replace(/\s/g, "");
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  ) as T;

export const updateCheckboxValue = (values: ContactFormValues): ContactFormValues =>
  Object.entries(values).reduce<ContactFormValues>((acc, [key, value]) => {
    if (typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "checked")) {
      acc[key] = value.checked ?? false;
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});

export const getSuccessFields = (fields: Array<FormInputType>, values: ContactFormValues) => {
  const emailFieldIndexes = Object.entries(values)
    .map(([_, value], i) => (typeof value == "string" && value.includes("@") ? i : -1))
    .filter((index) => index !== -1);
  const data = Object.entries(values);
  if (emailFieldIndexes.length > 1) {
    data.splice(emailFieldIndexes[1], 1);
  }

  return fields
    ? data
        .map(([id, value]) => {
          if (!value || typeof value === "boolean") return;
          const option = fields?.find((option) => option.id === id);
          return (
            option && {
              id: option.id,
              label: option.label,
              value: typeof value === "object" ? value.label : value.toString(),
            }
          );
        })
        .filter(Boolean)
    : [];
};

export const getConvertedChildrenData = (
  children: React.ReactNode,
  preFilledFields: PreFilledFieldsType,
  isPrefilledDisabled: boolean
) =>
  React.Children.map(children, (child: React.ReactNode) => {
    if (
      React.isValidElement<{
        blocks?: Array<{
          component?: {
            options?: {
              id?: string;
              isLocked?: boolean;
              mode?: "standard" | "editable";
            };
          };
        }>;
      }>(child)
    ) {
      const options = child.props.blocks?.[0]?.component?.options;
      if (options?.id && Object.values(preFilledFields).includes(options.id)) {
        options.isLocked = isPrefilledDisabled;
        options.mode = isPrefilledDisabled ? "standard" : "editable";
      }
      return child;
    }
  });
